import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client"; 
import { TaskForm } from "@/components/task/TaskForm";
import { Card, CardContent } from "@/components/ui/card";

export default function EditTask() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [task, setTask] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTask = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from('tasks')
          .select('*')
          .eq('id', id)
          .single();
          
        if (error) throw error;
        
        setTask(data);
      } catch (err: any) {
        setError(err.message || "Failed to load task");
        toast({
          title: "Error",
          description: "Failed to load task details",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchTask();
  }, [id, toast]);

  // Determine related_to_type from the task data
  const getRelatedToType = (task: any) => {
    if (task.organization_id) return "organization";
    if (task.contact_id) return "contact";
    return "none";
  };

  // Format task data for the form
  const formatTaskForForm = (task: any) => {
    return {
      ...task,
      related_to_type: getRelatedToType(task),
      due_date: task.due_date ? new Date(task.due_date) : undefined,
    };
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/tasks">
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Back to Tasks</span>
            </Link>
          </Button>
          <h1 className="text-2xl font-bold">Edit Task</h1>
        </div>
        <Card>
          <CardContent className="p-6 flex justify-center items-center min-h-[200px]">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/tasks">
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Back to Tasks</span>
            </Link>
          </Button>
          <h1 className="text-2xl font-bold">Edit Task</h1>
        </div>
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-destructive">{error || "Failed to load task"}</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => navigate("/tasks")}
            >
              Back to Tasks
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/tasks">
            <ArrowLeft className="h-4 w-4" />
            <span className="sr-only">Back to Tasks</span>
          </Link>
        </Button>
        <h1 className="text-2xl font-bold">Edit Task: {task.title}</h1>
      </div>
      
      <Card>
        <CardContent className="p-6">
          <TaskForm 
            defaultValues={formatTaskForForm(task)} 
            taskId={id} 
          />
        </CardContent>
      </Card>
    </div>
  );
}